'use client'

import ConfirmModal from '@/components/confirm-modal'
import {
    CreateEvolutionData,
    Diagnosis,
    MedicalEvolution,
    UpdateEvolutionData,
    VitalSigns,
    createEvolution,
    getDiagnosesByClient,
    getEvolution,
    signEvolution,
    updateEvolution,
} from '@/lib/api/prontuario/prontuario-api'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { useFormDraft } from '@/lib/form-draft/use-form-draft'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdCreate,
    MdSave,
    MdTrendingUp,
} from 'react-icons/md'
import { toast } from 'react-toastify'
import { useProntuarioNavigate, useProntuarioParams } from './prontuario-context'

interface FormData {
    professionalId: string
    evolutionDate: string
    subjective: string
    objective: string
    vitalSigns: VitalSigns
    assessment: string
    plan: string
    diagnosisIds: number[]
    examsRequested: string
    returnDate: string
    returnNotes: string
}

const defaultFormData: FormData = {
    professionalId: '',
    evolutionDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    subjective: '',
    objective: '',
    vitalSigns: {
        bloodPressureSystolic: undefined,
        bloodPressureDiastolic: undefined,
        heartRate: undefined,
        temperature: undefined,
        oxygenSaturation: undefined,
        respiratoryRate: undefined,
        weight: undefined,
        height: undefined,
        painLevel: undefined,
    },
    assessment: '',
    plan: '',
    diagnosisIds: [],
    examsRequested: '',
    returnDate: '',
    returnNotes: '',
}

export default function EvolucaoFormPage() {
    const navigate = useProntuarioNavigate()
    const { clientId, evolucaoId } = useProntuarioParams<{ clientId: string; evolucaoId: string }>()
    const { user, isProfessional } = useAuth()

    const [formData, setFormData] = useState<FormData>(defaultFormData)
    const [diagnoses, setDiagnoses] = useState<Diagnosis[]>([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [signing, setSigning] = useState(false)
    const [evolution, setEvolution] = useState<MedicalEvolution | null>(null)
    const [showSignModal, setShowSignModal] = useState(false)

    const isEditing = !!evolucaoId
    const basePath = `/person/clients/${clientId}/prontuario`

    /**
     * Rascunho: sair da evolução para agendar um exame (botão do prontuário) e voltar
     * não pode custar o texto já escrito. A chave separa paciente e registro — sem
     * isso o rascunho de um paciente reapareceria no de outro.
     *
     * Só liga depois do `loading`: antes disso o estado é o `defaultFormData`, e
     * gravá-lo apagaria o rascunho que estamos tentando recuperar.
     */
    const draft = useFormDraft(
        `evolucao:${clientId}:${evolucaoId ?? 'nova'}`,
        formData,
        setFormData,
        !loading
    )

    useEffect(() => {
        loadData()
    }, [clientId, evolucaoId])

    const loadData = async () => {
        if (!clientId) return

        setLoading(true)
        try {
            // Load client diagnoses
            const diagnosesResponse = await getDiagnosesByClient(parseInt(clientId, 10))
            if (diagnosesResponse.success) {
                setDiagnoses(diagnosesResponse.result || [])
            }

            // Load existing evolution if editing
            if (evolucaoId) {
                const response = await getEvolution(parseInt(evolucaoId, 10))
                if (response.success && response.result) {
                    const evo = response.result
                    setEvolution(evo)
                    setFormData({
                        professionalId: evo.professionalId?.toString() || evo.professional?.id?.toString() || '',
                        evolutionDate: evo.evolutionDate ? format(new Date(evo.evolutionDate), "yyyy-MM-dd'T'HH:mm") : '',
                        subjective: evo.subjective || '',
                        objective: evo.objective || '',
                        vitalSigns: evo.vitalSigns || defaultFormData.vitalSigns,
                        assessment: evo.assessment || '',
                        plan: evo.plan || '',
                        diagnosisIds: evo.diagnosisIds || [],
                        examsRequested: evo.examsRequested || '',
                        returnDate: evo.returnDate ? format(new Date(evo.returnDate), 'yyyy-MM-dd') : '',
                        returnNotes: evo.returnNotes || '',
                    })
                } else {
                    toast.error(response.error || 'Erro ao carregar evolução')
                    navigate(`${basePath}/evolucoes`)
                }
            }
        } catch (error) {
            toast.error('Erro ao carregar dados')
        } finally {
            setLoading(false)
        }
    }

    const updateField = (field: keyof FormData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }))
    }

    const updateVitalSign = (key: keyof VitalSigns, value: string) => {
        const numValue = value === '' ? undefined : parseFloat(value)
        setFormData(prev => ({
            ...prev,
            vitalSigns: { ...prev.vitalSigns, [key]: numValue },
        }))
    }

    const handleHeightBlur = () => {
        const h = formData.vitalSigns.height
        if (h !== undefined && h > 3) {
            setFormData(prev => ({
                ...prev,
                vitalSigns: { ...prev.vitalSigns, height: parseFloat((h / 100).toFixed(2)) },
            }))
        }
    }

    const toggleDiagnosis = (diagnosisId: number) => {
        setFormData(prev => ({
            ...prev,
            diagnosisIds: prev.diagnosisIds.includes(diagnosisId)
                ? prev.diagnosisIds.filter(id => id !== diagnosisId)
                : [...prev.diagnosisIds, diagnosisId],
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!clientId) return

        if (!isProfessional && !formData.professionalId) {
            toast.error('Selecione um profissional responsável')
            return
        }

        setSaving(true)
        try {
            // Format blood pressure
            const vitalSigns = { ...formData.vitalSigns }
            if (vitalSigns.bloodPressureSystolic && vitalSigns.bloodPressureDiastolic) {
                vitalSigns.bloodPressure = `${vitalSigns.bloodPressureSystolic}/${vitalSigns.bloodPressureDiastolic}`
            }

            if (isEditing && evolucaoId) {
                const updateData: UpdateEvolutionData = {
                    evolutionDate: formData.evolutionDate || undefined,
                    subjective: formData.subjective.trim() || undefined,
                    objective: formData.objective.trim() || undefined,
                    vitalSigns: vitalSigns,
                    assessment: formData.assessment.trim() || undefined,
                    plan: formData.plan.trim() || undefined,
                    diagnosisIds: formData.diagnosisIds.length > 0 ? formData.diagnosisIds : undefined,
                    examsRequested: formData.examsRequested.trim() || undefined,
                    returnDate: formData.returnDate || undefined,
                    returnNotes: formData.returnNotes.trim() || undefined,
                }

                const response = await updateEvolution(parseInt(evolucaoId, 10), updateData)
                if (response.success) {
                    toast.success('Evolução atualizada com sucesso!')
                    draft.clear()
                    navigate(`${basePath}/evolucoes`)
                } else {
                    toast.error(response.error || 'Erro ao atualizar evolução')
                }
            } else {
                const createData: CreateEvolutionData = {
                    clientId: parseInt(clientId, 10),
                    companyId: user!.companyId!,
                    professionalId: isProfessional ? user!.professionalId! : parseInt(formData.professionalId, 10),
                    evolutionDate: formData.evolutionDate || undefined,
                    subjective: formData.subjective.trim() || undefined,
                    objective: formData.objective.trim() || undefined,
                    vitalSigns: vitalSigns,
                    assessment: formData.assessment.trim() || undefined,
                    plan: formData.plan.trim() || undefined,
                    diagnosisIds: formData.diagnosisIds.length > 0 ? formData.diagnosisIds : undefined,
                    examsRequested: formData.examsRequested.trim() || undefined,
                    returnDate: formData.returnDate || undefined,
                    returnNotes: formData.returnNotes.trim() || undefined,
                }

                const response = await createEvolution(createData)
                if (response.success) {
                    toast.success('Evolução criada com sucesso!')
                    draft.clear()
                    navigate(`${basePath}/evolucoes`)
                } else {
                    toast.error(response.error || 'Erro ao criar evolução')
                }
            }
        } catch (error) {
            toast.error('Erro ao salvar evolução')
        } finally {
            setSaving(false)
        }
    }

    const handleSign = () => {
        setShowSignModal(true)
    }

    const confirmSign = async () => {
        if (!evolucaoId) return

        setSigning(true)
        try {
            const response = await signEvolution(parseInt(evolucaoId, 10))
            if (response.success) {
                toast.success('Evolução assinada com sucesso!')
                navigate(`${basePath}/evolucoes`)
            } else {
                toast.error(response.error || 'Erro ao assinar evolução')
            }
        } catch (error) {
            toast.error('Erro ao assinar evolução')
        } finally {
            setSigning(false)
            setShowSignModal(false)
        }
    }

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    const isSigned = !!evolution?.signedAt

    return (
        <div className="flex flex-col gap-6 max-w-4xl mx-auto">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate(`${basePath}/evolucoes`)}
                        className="btn btn-ghost btn-circle btn-sm"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-secondary/10 rounded-lg">
                            <MdTrendingUp className="text-2xl text-secondary" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-base-content">
                                {isEditing ? 'Editar Evolução' : 'Nova Evolução'}
                            </h1>
                            <p className="text-sm text-base-content/60">
                                Registro SOAP (Subjetivo, Objetivo, Avaliação, Plano)
                            </p>
                        </div>
                    </div>
                </div>
                {isEditing && !isSigned && (
                    <button
                        onClick={handleSign}
                        className="btn btn-success gap-2"
                        disabled={signing}
                    >
                        {signing ? (
                            <>
                                <span className="loading loading-spinner loading-sm"></span>
                                Assinando...
                            </>
                        ) : (
                            <>
                                <MdCreate className="text-lg" />
                                Assinar Digitalmente
                            </>
                        )}
                    </button>
                )}
            </div>

            {isSigned && (
                <div className="alert alert-info">
                    <span>Esta evolução foi assinada digitalmente e não pode ser editada.</span>
                </div>
            )}

            {/* Rascunho recuperado é AVISADO, nunca silencioso: restaurar texto
                clínico sem dizer faria o profissional assinar algo que ele acha que
                acabou de escrever. Com o aviso, ele confirma ou descarta. */}
            {draft.restored && (
                <div className="alert alert-warning">
                    <span>
                        Recuperamos o que você havia escrito
                        {draft.savedAt
                            ? ` em ${format(draft.savedAt, "dd/MM 'às' HH:mm", { locale: ptBR })}`
                            : ''}
                        . Confira antes de salvar.
                    </span>
                    <button
                        type="button"
                        className="btn btn-sm btn-ghost"
                        onClick={draft.discard}
                    >
                        Descartar rascunho
                    </button>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Professional */}
                {!isProfessional && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h3 className="text-lg font-bold text-base-content mb-4">Profissional Responsável</h3>
                        <div className="form-control w-full max-w-xs">
                            <ProfessionalSearchSelector
                                value={formData.professionalId ? Number(formData.professionalId) : null}
                                onChange={(id) => updateField('professionalId', id ? String(id) : '')}
                                label=""
                                placeholder="Selecione..."
                                disabled={isSigned || isEditing}
                            />
                        </div>
                    </div>
                )}

                {/* Date */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Data e Hora</h3>
                    <div className="form-control w-full max-w-xs">
                        <input
                            type="datetime-local"
                            className="input input-bordered w-full"
                            value={formData.evolutionDate}
                            onChange={e => updateField('evolutionDate', e.target.value)}
                            disabled={isSigned}
                        />
                    </div>
                </div>

                {/* Vital Signs */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Sinais Vitais</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium text-sm">PA Sistólica</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="120"
                                    value={formData.vitalSigns.bloodPressureSystolic || ''}
                                    onChange={e => updateVitalSign('bloodPressureSystolic', e.target.value)}
                                    disabled={isSigned}
                                />
                                <span className="bg-base-200 px-2 flex items-center text-sm">mmHg</span>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium text-sm">PA Diastólica</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="80"
                                    value={formData.vitalSigns.bloodPressureDiastolic || ''}
                                    onChange={e => updateVitalSign('bloodPressureDiastolic', e.target.value)}
                                    disabled={isSigned}
                                />
                                <span className="bg-base-200 px-2 flex items-center text-sm">mmHg</span>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium text-sm">FC</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="72"
                                    value={formData.vitalSigns.heartRate || ''}
                                    onChange={e => updateVitalSign('heartRate', e.target.value)}
                                    disabled={isSigned}
                                />
                                <span className="bg-base-200 px-2 flex items-center text-sm">bpm</span>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium text-sm">Temperatura</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    step="0.1"
                                    className="input input-bordered w-full"
                                    placeholder="36.5"
                                    value={formData.vitalSigns.temperature || ''}
                                    onChange={e => updateVitalSign('temperature', e.target.value)}
                                    disabled={isSigned}
                                />
                                <span className="bg-base-200 px-2 flex items-center text-sm">C</span>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium text-sm">SpO2</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="98"
                                    value={formData.vitalSigns.oxygenSaturation || ''}
                                    onChange={e => updateVitalSign('oxygenSaturation', e.target.value)}
                                    disabled={isSigned}
                                />
                                <span className="bg-base-200 px-2 flex items-center text-sm">%</span>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium text-sm">FR</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    className="input input-bordered w-full"
                                    placeholder="16"
                                    value={formData.vitalSigns.respiratoryRate || ''}
                                    onChange={e => updateVitalSign('respiratoryRate', e.target.value)}
                                    disabled={isSigned}
                                />
                                <span className="bg-base-200 px-2 flex items-center text-sm">irpm</span>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium text-sm">Peso</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    step="0.1"
                                    className="input input-bordered w-full"
                                    placeholder="70"
                                    value={formData.vitalSigns.weight || ''}
                                    onChange={e => updateVitalSign('weight', e.target.value)}
                                    disabled={isSigned}
                                />
                                <span className="bg-base-200 px-2 flex items-center text-sm">kg</span>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium text-sm">Altura (m)</span>
                            </label>
                            <div className="input-group">
                                <input
                                    type="number"
                                    step="0.01"
                                    className="input input-bordered w-full"
                                    placeholder="1.75"
                                    value={formData.vitalSigns.height || ''}
                                    onChange={e => updateVitalSign('height', e.target.value)}
                                    onBlur={handleHeightBlur}
                                    disabled={isSigned}
                                />
                                <span className="bg-base-200 px-2 flex items-center text-sm">m</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* SOAP */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">SOAP</h3>
                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    <span className="text-secondary font-bold">S</span> - Subjetivo (Queixa do Paciente)
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Queixas, sintomas relatados pelo paciente..."
                                rows={4}
                                value={formData.subjective}
                                onChange={e => updateField('subjective', e.target.value)}
                                disabled={isSigned}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    <span className="text-secondary font-bold">O</span> - Objetivo (Exame Físico)
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Achados do exame físico, dados objetivos..."
                                rows={4}
                                value={formData.objective}
                                onChange={e => updateField('objective', e.target.value)}
                                disabled={isSigned}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    <span className="text-accent font-bold">A</span> - Avaliação (Diagnóstico)
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Impressão diagnóstica, hipóteses..."
                                rows={4}
                                value={formData.assessment}
                                onChange={e => updateField('assessment', e.target.value)}
                                disabled={isSigned}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    <span className="text-info font-bold">P</span> - Plano (Conduta)
                                </span>
                            </label>
                            <textarea
                                className="textarea textarea-bordered w-full"
                                placeholder="Plano terapêutico, prescrições, orientações..."
                                rows={4}
                                value={formData.plan}
                                onChange={e => updateField('plan', e.target.value)}
                                disabled={isSigned}
                            />
                        </div>
                    </div>
                </div>

                {/* Diagnoses */}
                {diagnoses.length > 0 && (
                    <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                        <h3 className="text-lg font-bold text-base-content mb-4">Diagnósticos Relacionados</h3>
                        <div className="flex flex-wrap gap-2">
                            {diagnoses.map(diagnosis => (
                                <label
                                    key={diagnosis.id}
                                    className={`cursor-pointer badge badge-lg gap-2 ${formData.diagnosisIds.includes(diagnosis.id)
                                            ? 'badge-secondary'
                                            : 'badge-ghost'
                                        }`}
                                >
                                    <input
                                        type="checkbox"
                                        className="hidden"
                                        checked={formData.diagnosisIds.includes(diagnosis.id)}
                                        onChange={() => toggleDiagnosis(diagnosis.id)}
                                        disabled={isSigned}
                                    />
                                    {diagnosis.icdCode
                                        ? `${diagnosis.icdCode.code} - ${diagnosis.icdCode.description}`
                                        : diagnosis.customDiagnosis}
                                </label>
                            ))}
                        </div>
                    </div>
                )}

                {/* Exams Requested */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Exames Solicitados</h3>
                    <div className="form-control w-full">
                        <textarea
                            className="textarea textarea-bordered w-full"
                            placeholder="Liste os exames solicitados..."
                            rows={3}
                            value={formData.examsRequested}
                            onChange={e => updateField('examsRequested', e.target.value)}
                            disabled={isSigned}
                        />
                    </div>
                </div>

                {/* Return */}
                <div className="bg-base-100 rounded-xl border border-base-300/50 shadow-sm p-6">
                    <h3 className="text-lg font-bold text-base-content mb-4">Retorno</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Data do Retorno</span>
                            </label>
                            <input
                                type="date"
                                className="input input-bordered w-full"
                                value={formData.returnDate}
                                onChange={e => updateField('returnDate', e.target.value)}
                                disabled={isSigned}
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">Observações do Retorno</span>
                            </label>
                            <input
                                type="text"
                                className="input input-bordered w-full"
                                placeholder="Orientações para o retorno"
                                value={formData.returnNotes}
                                onChange={e => updateField('returnNotes', e.target.value)}
                                disabled={isSigned}
                            />
                        </div>
                    </div>
                </div>

                {/* Actions */}
                {!isSigned && (
                    <div className="flex items-center justify-end gap-3">
                        <button
                            type="button"
                            onClick={() => navigate(`${basePath}/evolucoes`)}
                            className="btn btn-ghost"
                            disabled={saving}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn btn-secondary gap-2"
                            disabled={saving}
                        >
                            {saving ? (
                                <>
                                    <span className="loading loading-spinner loading-sm"></span>
                                    Salvando...
                                </>
                            ) : (
                                <>
                                    <MdSave className="text-lg" />
                                    Salvar Evolução
                                </>
                            )}
                        </button>
                    </div>
                )}
            </form>

            <ConfirmModal
                isOpen={showSignModal}
                onClose={() => setShowSignModal(false)}
                onConfirm={confirmSign}
                title="Assinar evolução"
                message="Deseja assinar digitalmente esta evolução? Após a assinatura, não será possível editar."
                confirmButtonText="Assinar"
                cancelButtonText="Cancelar"
                loading={signing}
            />
        </div>
    )
}
